import { getCurrentInstance, isRef } from "vue";

export function useVm() {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error("useVm must be called within a component setup function.");
  }

  const vmExpose = (value: Record<string, unknown>) => {
    Object.keys(value).forEach((key) => {
      if (instance.proxy) {
        // Override computed if any
        if (instance.proxy.$options?.computed?.[key]) {
          const computedValueGetter = {
            get: () => (isRef(value[key]) ? value[key].value : value[key]),
          };
          instance.proxy.$options.computed[key] = computedValueGetter;
          if (key in instance.proxy) {
            delete instance.proxy[key];
          } else {
            instance.proxy[key] = computedValueGetter;
          }
          return;
        }

        // Override method if any
        if (instance.proxy.$options?.methods?.[key]) {
          instance.proxy.$options.methods[key] = value[key];
          return;
        }

        // Override data if any
        if (instance.proxy.$options?.data) {
          const dataFn = instance.proxy.$options.data as () => Record<string, unknown>;
          const dataObj = dataFn();
          if (key in dataObj) {
            instance.proxy.$options.data = () => ({ ...dataObj, [key]: value[key] });
            instance.proxy[key] = value[key];
            return;
          }
        }
        // Otherwise, create getter/setter if it's a ref
        if (isRef(value[key])) {
          Object.defineProperty(instance.proxy, key, {
            get() {
              return value[key].value;
            },
            set(newValue) {
              value[key].value = newValue;
            },
            enumerable: true,
            configurable: true,
          });
          return;
        }

        instance.proxy[key] = value[key];
      }
    });
  };

  if (instance.proxy) {
    return {
      vm: instance.proxy,
      vmExpose,
      vmNode: instance.vnode,
    };
  }
  throw new Error("No proxy");
}
